import React from 'react';
import { Building2, FileText, Mail, MessageSquare, Phone, UserCircle, Users, X } from 'lucide-react';

interface JobHeaderProps {
  displayTitle: string;
  activeContact: any;
  onClose: () => void;
  onOpenContacts?: () => void;
  onOpenQuotes?: () => void;
  contactCount?: number;
  quoteCount?: number;
}

export const JobHeader: React.FC<JobHeaderProps> = ({ displayTitle, activeContact, onClose, onOpenContacts, onOpenQuotes, contactCount = 0, quoteCount = 0 }) => (
  <header className="relative z-[180] flex h-[4.25rem] w-full min-w-0 max-w-full shrink-0 items-center justify-between gap-3 overflow-hidden border-b border-white/5 bg-slate-950/95 px-3 py-3 sm:h-auto sm:px-5 lg:px-8" data-testid="job-header">
    <div className="flex gap-3 sm:gap-4 items-center min-w-0">
      <div className="hidden sm:flex w-11 h-11 lg:w-12 lg:h-12 rounded-xl bg-brand-neonBlue/10 border border-brand-neonBlue/20 items-center justify-center text-brand-neonBlue shrink-0">
        {displayTitle !== 'Private Account' ? <Building2 size={24} /> : <UserCircle size={24} />}
      </div>
      <div className="min-w-0">
        <h3 className="type-display text-xl text-white sm:text-2xl lg:text-3xl truncate sm:whitespace-normal sm:break-words">{displayTitle}</h3>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <p className="type-label hidden max-w-[26rem] truncate text-slate-500 sm:block">
            Focus: {activeContact?.first_name || activeContact?.firstName} {activeContact?.last_name || activeContact?.lastName}
          </p>
          {(activeContact?.email || activeContact?.clientEmail) && (
            <a
              href={`https://mail.google.com/mail/?view=cm&fs=1&to=${activeContact.email || activeContact.clientEmail}`}
              target="quikpix_gmail_compose"
              rel="noopener noreferrer"
              className="type-button hidden min-h-11 items-center gap-1.5 rounded-md border border-brand-neonBlue/20 bg-brand-neonBlue/10 px-3 py-2 text-xs text-brand-neonBlue shadow-sm transition-colors hover:bg-brand-neonBlue/20 md:flex"
              title="Compose in Gmail"
            >
              <Mail size={12} /> Email
            </a>
          )}
          {(activeContact?.phone || activeContact?.clientPhone) && (
            <div className="flex items-center gap-2">
              <a href={`tel:${activeContact.phone || activeContact.clientPhone}`} className="type-button hidden min-h-11 items-center gap-1.5 rounded-md border border-green-500/20 bg-green-500/10 px-3 py-2 text-xs text-green-400 shadow-sm transition-colors hover:bg-green-500/20 md:flex" title="Call Number">
                <Phone size={12} /> Call
              </a>
              <a href={`sms:${activeContact.phone || activeContact.clientPhone}`} className="type-button hidden min-h-11 items-center gap-1.5 rounded-md border border-brand-neonPink/20 bg-brand-neonPink/10 px-3 py-2 text-xs text-brand-neonPink shadow-sm transition-colors hover:bg-brand-neonPink/20 md:flex" title="Send Text Message">
                <MessageSquare size={12} /> SMS
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <button onClick={onOpenContacts} className="2xl:hidden min-h-11 px-3 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-brand-neonBlue flex items-center gap-1.5" aria-label={`Open contacts, ${contactCount} available`}><Users size={18} /><span className="hidden sm:inline text-xs font-bold">{contactCount}</span></button>
      <button onClick={onOpenQuotes} className="2xl:hidden min-h-11 px-3 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-brand-neonBlue flex items-center gap-1.5" aria-label={`Open quotes, ${quoteCount} available`}><FileText size={18} /><span className="hidden sm:inline text-xs font-bold">{quoteCount}</span></button>
      <button onClick={onClose} className="min-w-11 min-h-11 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition-all focus-visible:ring-2 focus-visible:ring-brand-neonBlue" aria-label="Close job details"><X size={24} /></button>
    </div>
  </header>
);
